/**
 * frontDesk.service.ts
 *
 * Typed API call wrappers for the front desk tap pads.
 *
 * A desk is one physical check-in point: a sticker or reader pad carrying a
 * link, and the browser on the desk that watches for taps. Admins create and
 * rename desks and can issue a new link for one when a sticker is lost; any
 * staff member can list them so the operator of a front-desk PC can pick which
 * desk this computer is sitting at.
 *
 * An arrival is one tap. The desk's browser polls for arrivals nobody has
 * picked up yet and claims the ones it finds; the claim is what decides which
 * single browser tab opens the patient's chart.
 *
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Domain types ───────────────────────────────────────────────────────────

export interface FrontDeskDesk {
  deskID: number;
  name: string;
  /** 0 means the desk is not tied to one facility. */
  facilityID: number;
  facilityName: string;
  active: boolean;
  /** The link written to the sticker or pushed to the reader pad. */
  tapUrl: string;
  /** When this desk last saw a tap, or null if it never has. */
  lastArrivalAtUtc: string | null;
}

/**
 * Returned only by create and by issuing a new link — the value that goes on
 * the sticker. Listing desks never returns it.
 */
export type FrontDeskDeskWithToken = FrontDeskDesk & { deskToken: string };

export interface CreateDeskInput {
  name: string;
  facilityID: number;
}

export interface UpdateDeskInput {
  name?: string;
  facilityID?: number;
  active?: boolean;
}

/**
 * One unclaimed tap. The patient is identified to the desk by first name and
 * last initial only — enough for staff to recognise who walked in, and no more
 * than that on a screen the waiting room can see.
 */
export interface FrontDeskArrival {
  arrivalID: number;
  patientID: number;
  patientFirstName: string;
  patientLastInitial: string;
  arrivedAtUtc: string;
  /** Whether this arrival checked today's visit in. */
  checkedIn: boolean;
  appointmentID: number | null;
}

export interface FrontDeskClaim {
  arrivalID: number;
  patientID: number;
  checkedIn: boolean;
  appointmentID: number | null;
  /** App-relative path, e.g. "/patients/123?section=appointments". */
  redirect: string;
}

// ─── Service methods ────────────────────────────────────────────────────────

/** Every desk in the caller's account. Available to any staff member. */
export function listDesks(): Promise<FrontDeskDesk[]> {
  return apiClient
    .get<ApiResponse<FrontDeskDesk[]>>('/checkin/desks')
    .then((r) => r.data.data);
}

/** Admin only. Returns the new desk including the link for its sticker. */
export function createDesk(input: CreateDeskInput): Promise<FrontDeskDeskWithToken> {
  return apiClient
    .post<ApiResponse<FrontDeskDeskWithToken>>('/checkin/desks', input)
    .then((r) => r.data.data);
}

/** Admin only. Rename, move to another facility, or deactivate a desk. */
export function updateDesk(deskID: number, input: UpdateDeskInput): Promise<FrontDeskDesk> {
  return apiClient
    .patch<ApiResponse<FrontDeskDesk>>(`/checkin/desks/${deskID}`, input)
    .then((r) => r.data.data);
}

/**
 * Admin only. Issues a new link for the desk; anything printed with the old
 * one stops working, so the caller is expected to confirm first.
 */
export function rotateDeskToken(deskID: number): Promise<FrontDeskDeskWithToken> {
  return apiClient
    .post<ApiResponse<FrontDeskDeskWithToken>>(`/checkin/desks/${deskID}/rotate-token`)
    .then((r) => r.data.data);
}

/**
 * Taps at this desk that nobody has picked up yet, newer than `since`
 * (an arrivalID; 0 asks for everything still open).
 */
export function pollArrivals(deskID: number, since: number): Promise<FrontDeskArrival[]> {
  return apiClient
    .get<ApiResponse<FrontDeskArrival[]>>(`/checkin/desks/${deskID}/arrivals`, {
      params: { since },
    })
    .then((r) => r.data.data);
}

/**
 * Take ownership of one arrival. Exactly one caller succeeds; everyone else
 * is rejected, which is the normal outcome for every browser tab but one.
 */
export function claimArrival(arrivalID: number, tabID: string): Promise<FrontDeskClaim> {
  return apiClient
    .post<ApiResponse<FrontDeskClaim>>(`/checkin/arrivals/${arrivalID}/claim`, { tabID })
    .then((r) => r.data.data);
}
